
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BarChart3, Calendar, ChevronLeft, ChevronRight, TrendingUp, 
  Dumbbell, Utensils, Award, Zap, Download, Share2, Lock, Sparkles,
  ArrowUpRight, ArrowDownRight, Clock
} from 'lucide-react';
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, 
  CartesianGrid, Tooltip, PieChart, Pie 
} from 'recharts';
import { User, WeeklyStats } from './types';

// Calculate current week ID from real date
const getCurrentWeekId = (): string => {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor((now.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);
  return `W${weekNumber}`;
};

// Get month name from date
const getMonthName = (date: Date): string => {
  return date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }).toUpperCase();
};

export const ReportsDashboard = ({ user }: { user: User }) => {
  const { t } = useTranslation();
  const [weeklyHistory] = useState<WeeklyStats[]>([]); // Real data from user activity
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [hasRealData, setHasRealData] = useState(false);
  
  // Calculate current week info
  const currentWeekId = getCurrentWeekId();
  const currentDate = new Date();
  const currentMonthYear = getMonthName(currentDate);
  
  // Check if user has any real activity data
  useEffect(() => {
    // Check if user has completed any daily actions or has XP
    const hasActivity = (user.dailyCoreActionCompleted === true && user.dailyCoreActionDate) || 
                        (user.xp && user.xp > 0) ||
                        (user.monthlyCalendars && user.monthlyCalendars.some(cal => cal.totalStamps > 0));
    
    setHasRealData(Boolean(hasActivity));
    
    // In a real implementation, you would fetch weekly stats from Firestore here
    // For now, we'll show empty states if no activity exists
  }, [user]);

  const currentWeek = weeklyHistory[selectedWeekIndex] || null;
  // Admin hanno accesso completo, anche con piano free
  const isPremium = user.role === 'admin' || user.plan !== 'free';

  return (
    <div className="p-4 md:p-8 pb-32 space-y-8 max-w-6xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3">
            <BarChart3 className="text-emerald-400" size={32} /> {t('reports.title')}
          </h1>
          <p className="text-slate-500 text-sm font-medium">{t('reports.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button className="flex items-center gap-2 bg-slate-900 border border-slate-800 px-4 py-2 rounded-xl text-xs font-bold hover:bg-slate-800 transition-all">
            <Download size={14}/> {t('reports.export_pdf')}
          </button>
          <button className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-xl text-xs font-black shadow-lg shadow-emerald-500/20">
            <Share2 size={14}/> {t('reports.share_stats')}
          </button>
        </div>
      </div>

      {/* Week Selector */}
      <div className="bg-slate-900 border border-slate-800 p-4 rounded-3xl flex items-center justify-between">
        <button 
          onClick={() => setSelectedWeekIndex(prev => Math.max(0, prev - 1))}
          className="p-2 hover:bg-slate-800 rounded-xl transition-all disabled:opacity-30"
          disabled={selectedWeekIndex === 0 || weeklyHistory.length === 0}
        >
          <ChevronLeft/>
        </button>
        <div className="text-center">
          <span className="text-[10px] font-black text-emerald-500 uppercase tracking-widest block">{t('reports.selected_week')}</span>
          <span className="text-lg font-black text-white">{currentWeekId} • {currentMonthYear}</span>
        </div>
        <button 
          onClick={() => setSelectedWeekIndex(prev => Math.min(weeklyHistory.length - 1, prev + 1))}
          className="p-2 hover:bg-slate-800 rounded-xl transition-all disabled:opacity-30"
          disabled={selectedWeekIndex === weeklyHistory.length - 1 || weeklyHistory.length === 0}
        >
          <ChevronRight/>
        </button>
      </div>

      {/* Empty State - No Data Yet */}
      {!hasRealData && (
        <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-12 text-center space-y-4">
          <BarChart3 className="text-slate-600 mx-auto" size={64} />
          <h2 className="text-xl font-black text-slate-300">Your reports will appear once you start completing actions.</h2>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Complete daily actions or challenges to generate insights.
          </p>
        </div>
      )}

      {/* Real Data View */}
      {hasRealData && (
        <>
          {/* Primary Stats Grid */}
      <div className="grid md:grid-cols-4 gap-6">
        {currentWeek ? (
          <>
            <ReportStatCard 
              label={t('reports.xp_gained')} 
              value={`+${currentWeek.totalXP}`} 
              trend={currentWeek.xpDiffPercent} 
              icon={<Zap className="text-yellow-500"/>} 
            />
            <ReportStatCard 
              label={t('reports.active_days')} 
              value={`${currentWeek.daysActive}/7`} 
              sub={t('reports.days_this_week')} 
              icon={<Calendar className="text-blue-500"/>} 
            />
            <ReportStatCard 
              label={t('reports.workouts')} 
              value={currentWeek.workouts} 
              sub={t('reports.sessions_completed')} 
              icon={<Dumbbell className="text-emerald-500"/>} 
            />
            <ReportStatCard 
              label={t('reports.rank_change')} 
              value={currentWeek.rankChange > 0 ? `+${currentWeek.rankChange}` : currentWeek.rankChange} 
              sub={t('reports.positions_ranking')} 
              icon={<TrendingUp className="text-cyan-500"/>} 
            />
          </>
        ) : (
          <>
            <EmptyStatCard 
              label={t('reports.xp_gained')} 
              unlockText="Unlocks after your first completed action"
              icon={<Zap className="text-yellow-500"/>} 
            />
            <EmptyStatCard 
              label={t('reports.active_days')} 
              unlockText="Unlocks after completing daily actions"
              icon={<Calendar className="text-blue-500"/>} 
            />
            <EmptyStatCard 
              label={t('reports.workouts')} 
              unlockText="Unlocks after logging workouts"
              icon={<Dumbbell className="text-emerald-500"/>} 
            />
            <EmptyStatCard 
              label={t('reports.rank_change')} 
              unlockText="Unlocks after joining the leaderboard"
              icon={<TrendingUp className="text-cyan-500"/>} 
            />
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Chart Area */}
        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="font-black text-xl">{t('reports.xp_progression')}</h2>
            <div className="bg-slate-800 p-1 rounded-xl flex">
              <button className="px-3 py-1 bg-emerald-500 text-white text-[10px] font-black rounded-lg uppercase">{t('reports.daily')}</button>
              <button className="px-3 py-1 text-slate-500 text-[10px] font-black rounded-lg uppercase">{t('reports.weekly')}</button>
            </div>
          </div>
          
          {weeklyHistory.length > 0 ? (
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={weeklyHistory}>
                  <defs>
                    <linearGradient id="colorXP" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                  <XAxis dataKey="weekId" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '16px' }}
                  />
                  <Area type="monotone" dataKey="totalXP" stroke="#10b981" fillOpacity={1} fill="url(#colorXP)" strokeWidth={4} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[300px] w-full flex items-center justify-center bg-slate-800/30 rounded-2xl border border-slate-800">
              <div className="text-center space-y-3">
                <TrendingUp className="text-slate-600 mx-auto" size={48} />
                <p className="text-slate-400 font-bold">No progression yet. Start today.</p>
                <p className="text-xs text-slate-600">Complete daily actions to see your XP progression chart.</p>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar Insights */}
        <div className="space-y-6">
          {/* AI Coach Insight */}
          {hasRealData && currentWeek ? (
            <div className="bg-gradient-to-br from-emerald-900/40 to-slate-900 border border-emerald-500/20 rounded-[2rem] p-6 relative overflow-hidden">
              <Sparkles className="absolute top-2 right-2 text-emerald-500/20" size={64}/>
              <div className="relative z-10">
                <h3 className="text-emerald-400 font-black text-xs uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Zap size={14} fill="currentColor"/> {t('reports.ai_insight')}
                </h3>
                <p className="text-sm font-medium leading-relaxed text-slate-200">
                  {t('reports.ai_insight_text', { name: user.name })}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-6 text-center space-y-3">
              <Sparkles className="text-slate-600 mx-auto" size={32} />
              <h3 className="text-slate-400 font-black text-xs uppercase tracking-widest">{t('reports.ai_insight')}</h3>
              <p className="text-sm text-slate-500">AI insights unlock after consistent activity.</p>
            </div>
          )}

          {/* Premium Gating Card */}
          {!isPremium && (
            <div className="bg-slate-900 border border-indigo-500/30 rounded-[2rem] p-6 text-center space-y-4 shadow-xl">
              <div className="w-12 h-12 bg-indigo-500/10 text-indigo-400 rounded-2xl flex items-center justify-center mx-auto">
                <Lock size={24}/>
              </div>
              <h3 className="font-black text-sm uppercase tracking-widest">{t('reports.unlock_advanced')}</h3>
              <p className="text-[10px] text-slate-500 font-medium">{t('reports.advanced_desc')}</p>
              <button className="w-full bg-indigo-500 text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-all">
                {t('reports.upgrade_now')}
              </button>
            </div>
          )}

          {/* Badges this week */}
          <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-6">
            <h3 className="font-black text-xs uppercase tracking-widest text-slate-500 mb-4">{t('reports.badges_obtained')}</h3>
            <div className="flex gap-2">
              {currentWeek && currentWeek.badgesUnlocked.length > 0 ? (
                currentWeek.badgesUnlocked.map(b => (
                  <div key={b} className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center text-xl shadow-lg border border-emerald-500/20">
                    🔥
                  </div>
                ))
              ) : (
                <p className="text-[10px] text-slate-600 italic font-medium">{t('reports.no_badges_week')}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Breakdown - Only for Premium */}
      {hasRealData && (
        <div className={`bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 relative overflow-hidden ${!isPremium ? 'opacity-50 blur-[2px] pointer-events-none' : ''}`}>
          {!isPremium && (
            <div className="absolute inset-0 flex items-center justify-center z-20">
              <div className="bg-slate-950/80 backdrop-blur-md px-6 py-3 rounded-2xl border border-slate-800 flex items-center gap-2">
                <Lock size={16} className="text-indigo-400"/>
                <span className="text-xs font-black uppercase tracking-widest">{t('reports.premium_feature')}</span>
              </div>
            </div>
          )}
          <h2 className="font-black text-xl mb-8 flex items-center gap-3">
            <Clock className="text-emerald-400"/> {t('reports.activity_allocation')}
          </h2>
          {/* Activity allocation requires real tracking data - show empty state until implemented */}
          <div className="text-center py-12 space-y-3">
            <Clock className="text-slate-600 mx-auto" size={48} />
            <p className="text-slate-400 font-bold">Activity allocation data will appear here</p>
            <p className="text-xs text-slate-600">Start tracking activities to see your allocation breakdown.</p>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

const EmptyStatCard = ({ label, unlockText, icon }: { label: string; unlockText: string; icon: React.ReactNode }) => (
  <div className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] hover:border-slate-700 transition-all">
    <div className="flex justify-between items-start mb-4">
      <div className="p-3 bg-slate-800 rounded-2xl opacity-50">{icon}</div>
    </div>
    <div className="space-y-2">
      <div className="text-lg font-black text-slate-600">—</div>
      <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
        {label}
      </div>
      <p className="text-[9px] text-slate-600 font-medium italic mt-2">{unlockText}</p>
    </div>
  </div>
);

interface ReportStatCardProps {
  label: string;
  value: string | number;
  trend?: number;
  sub?: string;
  icon: React.ReactNode;
}

const ReportStatCard = ({ label, value, trend, sub, icon }: ReportStatCardProps) => (
  <div className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] hover:border-emerald-500/30 transition-all group">
    <div className="flex justify-between items-start mb-4">
      <div className="p-3 bg-slate-800 rounded-2xl group-hover:scale-110 transition-transform">{icon}</div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-[10px] font-black px-2 py-1 rounded-lg ${trend >= 0 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
          {trend >= 0 ? <ArrowUpRight size={12}/> : <ArrowDownRight size={12}/>}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <div className="text-3xl font-black text-white">{value}</div>
    <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">
      {label}
    </div>
    {sub && <p className="text-[10px] text-slate-600 font-bold mt-2 italic">{sub}</p>}
  </div>
);

interface ActivityRowProps {
  label: string;
  percent: number;
  color: string;
}

const ActivityRow = ({ label, percent, color }: ActivityRowProps) => (
  <div className="space-y-1.5">
    <div className="flex justify-between items-end">
      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
      <span className="text-xs font-black text-white">{percent}%</span>
    </div>
    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
      <div className={`h-full ${color} transition-all duration-1000`} style={{ width: `${percent}%` }} />
    </div>
  </div>
);
